/**
 * Storefront PASSWORD GATE -- the store-wide content wall.
 *
 * Jake's requirement: "If the store has a password gate ('shared password' in
 * store login), then you shouldn't be able to get to ANY page in the store
 * without entering that shared password first. Once you're in the actual
 * store, we need full shopper account capabilities, but they're optional --
 * you can check out as a guest."
 *
 * WHAT WAS BROKEN. The gate used to be a COMPONENT (StorefrontSessionGate)
 * mounted at the `/shop/:slug` route record in router/groups/storefront.js.
 * Its CHILDREN -- Home, Category, Product -- were covered. But the shopper
 * session routes (`/shop/:slug/cart`, `/orders`, `/orders/:id`, `/profile`,
 * `/addresses`, `/payment-methods`) are SIBLING route records, not children.
 * They never mounted the gate at all, so a visitor to a password-protected
 * store could open the cart -- and every other session surface -- without ever
 * being asked for the password.
 *
 * WHY A GUARD RATHER THAN A COMPONENT. Three reasons:
 *
 *   1. COVERAGE BY CONSTRUCTION. A component gates only its own subtree, so
 *      protection depends on every future route being nested under the right
 *      parent -- exactly the thing that failed. This guard matches on the
 *      PATH (`/shop/:slug/...`), so a route added tomorrow is covered without
 *      anyone remembering to nest it.
 *
 *   2. NO FLASH OF CONTENT. The component mounted, THEN ran two async
 *      round-trips before deciding what to show. A guard resolves BEFORE the
 *      destination component is created, so store content is never
 *      instantiated -- not briefly, not behind an overlay.
 *
 *   3. ONE MECHANISM PER CONCEPT (docs/design-philosophy.md #2). "Is this
 *      visitor allowed into this store?" is one product question. Answering
 *      it in each page component is the same question answered six times, and
 *      the seventh page ships unprotected.
 *
 * WHAT PASSING THE PASSWORD DOES -- AND DOES NOT DO.
 *
 * It marks the visitor as CLEARED FOR THIS STORE'S CONTENT. It does NOT log
 * them in. This is forced by the backend: `validate_store_password`
 * (api/src/endpoints/campaign.py) accepts only `{password, campaign_id}` and
 * compares against the store's shared shopper account password. It receives no
 * user identifier, so it cannot establish who the visitor IS -- only that they
 * know the store's shared secret. Swapping the visitor onto the shared shopper
 * account (what the old flow did) would hand every visitor the SAME identity,
 * and with it that account's order history and saved addresses. The visitor
 * stays anonymous; identity is collected at checkout.
 *
 * THREAT MODEL of the cleared marker. The marker is a localStorage flag, so a
 * visitor can obviously set it by hand. Since card nrNUCjgU (2026-10-08) that
 * buys them nothing: the API enforces the store password itself, refusing a
 * password store's content (and payroll orders) to a session without a
 * server-side gate pass, which only a correct password mints. The marker is
 * now a UX hint that skips the wall for a visitor who already passed; when the
 * server disagrees, its 403 `store_gate_required` drops the marker and sends
 * the visitor back here (interceptors/authResponseInterceptor.js).
 *
 * Behavioural tests: `__tests__/storefront-password-gate.spec.js` (guard as a
 * function) and `__tests__/storefront-password-gate-real-router.spec.js` (real
 * router; asserts NO store page component is ever created pre-password).
 */

import { isStorefrontPreview } from "./storefrontPreview";
import { isPrivilegedSession } from "./privilegedSession";

/**
 * Namespace for the per-store cleared markers. The store slug is appended, so
 * the fact is scoped to ONE store: clearing store A's gate leaves store B
 * walled.
 */
export const STORE_GATE_PREFIX = "storeGateCleared:";

/** Route name of the password wall. */
export const PASSWORD_GATE_ROUTE = "storefront_password_gate";

/**
 * Marker VALUE. Deliberately a constant, content-free string: the marker
 * records one bit ("this browser cleared this store's wall") and must never
 * become a place where something credential-shaped accumulates. A test pins
 * that it contains no token / account / role text.
 */
const CLEARED_VALUE = "1";

function readLocal(key) {
    try {
        if (typeof localStorage?.getItem === "function") {
            return localStorage.getItem(key);
        }
    } catch {
        // storage unavailable -> treat as absent (fails closed: an
        // unreadable marker means "not cleared", so the wall stays up)
    }
    return null;
}

function writeLocal(key, value) {
    try {
        if (typeof localStorage?.setItem === "function") {
            localStorage.setItem(key, value);
        }
    } catch {
        // storage unavailable -> the visitor is re-prompted on the next
        // navigation. Annoying, never unsafe.
    }
}

/** The per-store localStorage key for the cleared marker. */
export function storeGateKey(slug) {
    return `${STORE_GATE_PREFIX}${slug}`;
}

/** True when THIS browser has cleared THIS store's password wall. */
export function isStoreGateCleared(slug) {
    if (!slug) return false;
    return readLocal(storeGateKey(slug)) === CLEARED_VALUE;
}

/**
 * Record that the visitor cleared this store's wall.
 *
 * Touches only its own namespaced key -- never `token` or `userId`. Clearing
 * the gate is not a login and must not look like one to any other code
 * reading the session.
 */
export function markStoreGateCleared(slug) {
    if (!slug) return;
    writeLocal(storeGateKey(slug), CLEARED_VALUE);
}

/** Forget a cleared gate (used when a store's password stops matching). */
export function clearStoreGate(slug) {
    if (!slug) return;
    try {
        localStorage?.removeItem?.(storeGateKey(slug));
    } catch {
        // nothing to do -- an unremovable marker only over-permits CONTENT
    }
}

/**
 * The storefront tree this guard defends: `/shop` and everything under it.
 *
 * The legacy `/store/...` entry paths are deliberately NOT listed. They are
 * pure redirect records (router/redirects.js) that forward into `/shop/...`,
 * and the guard runs again on the resolved destination -- so they are covered
 * by their target, not by a second pattern here. Matching both would be two
 * mechanisms for one question.
 */
function isStorefrontPath(path) {
    return (
        typeof path === "string" &&
        (path === "/shop" || path.startsWith("/shop/"))
    );
}

/**
 * The store slug for a storefront route.
 *
 * Read from the route PARAM only -- deliberately NOT from the
 * `localStorage.storeUrl` fallback that resolveStorefrontSlug() uses. That
 * fallback exists to help in-store navigation remember where it is; letting it
 * feed the GATE would mean a stale `storeUrl` from store A could pick store
 * A's cleared marker while the visitor is actually navigating store B.
 * Authority for "which store is this" comes from the URL being navigated to.
 */
function slugForRoute(route) {
    const fromParam = route?.params?.slug;
    if (typeof fromParam === "string" && fromParam.length > 0) {
        return fromParam;
    }
    return null;
}

/**
 * Default transport: the two campaign endpoints the wall needs. Injected in
 * tests so the guard can be exercised without a network.
 */
export function createAxiosLookup(axios) {
    return {
        async getCampaignId(slug) {
            const resp = await axios.get(
                `/campaign/getCampaignIdFromShopperURL/${slug}`
            );
            return resp?.data ?? null;
        },
        async getSignInInfo(campaignId) {
            const resp = await axios.post(`/campaign/getCampaignSignInInfo`, {
                campaign_id: campaignId,
            });
            return resp?.data ?? null;
        },
    };
}

/**
 * Per-slug cache of "is this store password protected?".
 *
 * Without it, EVERY in-store navigation pays two network round-trips before
 * the page can render. The cache holds only the public protected-or-not bit
 * (the same bit `getCampaignSignInInfo` serves to anyone who asks), never the
 * password and never a session. It lives for the page session; a reload
 * re-asks, which is the correct behaviour when a distributor toggles the
 * store's password.
 */
function createProtectionCache() {
    const cache = new Map();
    return {
        get: (slug) => cache.get(slug),
        set: (slug, value) => cache.set(slug, value),
        has: (slug) => cache.has(slug),
    };
}

/**
 * Build the `router.beforeEach` guard.
 *
 * Resolution order, and why each step comes where it does:
 *
 *   1. NOT a storefront path -> allow. Costs nothing, and keeps the guard
 *      inert for the other three apps.
 *   2. The wall itself -> allow. Otherwise the redirect below loops.
 *   3. `?preview=1` -> allow. The store editor previews a password-protected
 *      store WITHOUT being prompted; it renders read-only against the editor's
 *      own session and must not be walled out of its own store.
 *   4. Privileged session -> allow. A back-office user or an impersonating SA
 *      keeps their session and sees their own store. Their authorization is
 *      the bearer token, which the wall knows nothing about.
 *   5. Already cleared for THIS store -> allow. This is what stops the
 *      re-prompt on every click and across a reload.
 *   6. Ask whether the store is protected. Unprotected -> allow (an open store
 *      is unchanged). Protected -> redirect to the wall.
 *
 * ON A LOOKUP FAILURE the guard distinguishes two cases that a single
 * "fail closed" rule would conflate (docs/design-philosophy.md #5 -- fail
 * toward the record, but for the UNUSUAL data, not just the common case):
 *
 *   - We ALREADY KNOW this store is protected (a cached answer, or an earlier
 *     navigation established it) -> WALL. Losing the ability to re-confirm
 *     must never downgrade a known-protected store to an open one.
 *
 *   - We have NEVER established whether this store is protected -> ALLOW
 *     through to the destination. Walling here would be worse than useless: on
 *     a store that has NO password we would show a password prompt that no
 *     password can satisfy, turning an API blip into a hard lockout of the
 *     COMMON case. The destination page needs the same API we just failed to
 *     reach, so it renders its own error rather than a misleading wall, and
 *     the check re-runs on the next navigation.
 *
 * The asymmetry is the point: an unknown store is not treated as protected,
 * but a known-protected store is never treated as unknown.
 */
export function createStorefrontPasswordGate(lookup, { cache } = {}) {
    const protection = cache ?? createProtectionCache();

    return async function storefrontPasswordGate(to) {
        if (!isStorefrontPath(to?.path)) return true;
        if (to?.name === PASSWORD_GATE_ROUTE) return true;
        if (isStorefrontPreview(to)) return true;
        if (isPrivilegedSession()) return true;

        const slug = slugForRoute(to);
        // A storefront path with no resolvable slug (e.g. bare `/shop`) has no
        // store to gate; leave it to the route's own handling.
        if (!slug) return true;

        if (isStoreGateCleared(slug)) return true;

        let isProtected;
        if (protection.has(slug)) {
            isProtected = protection.get(slug);
        } else {
            try {
                const campaignId = await lookup.getCampaignId(slug);
                if (!campaignId) {
                    // No such store. Not this guard's job to render the
                    // not-found state -- let the destination handle it.
                    return true;
                }
                const info = await lookup.getSignInInfo(campaignId);
                isProtected = Boolean(info?.is_password_protected);
                protection.set(slug, isProtected);
            } catch {
                // The lookup could not complete. We have no cached answer for
                // this slug (or we would not be in this branch), so this store
                // is UNKNOWN, not known-protected.
                //
                // Allow through. A wall here would prompt for a password on a
                // store that may well have none -- an unanswerable prompt, and
                // a hard lockout of every open store whenever the API blips.
                // The destination needs this same API, so it surfaces the real
                // error; the check re-runs on the next navigation.
                //
                // A store already KNOWN to be protected never reaches this
                // branch: its cached `true` is served above, so a failed
                // re-confirmation cannot downgrade it to open.
                return true;
            }
        }

        if (!isProtected) return true;

        return { name: PASSWORD_GATE_ROUTE, params: { slug } };
    };
}

export default createStorefrontPasswordGate;
